/**
 * Shared SaaS app shell + UI primitives.
 * Coursolo admin look: Plus Jakarta, slate neutrals, single brand blue.
 */

import React, { useEffect, useRef, useState, startTransition } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../branding/OrganizationContext';
import OrgBrandLogo from '../branding/OrgBrandLogo';
import { shouldShowBrandNameWithLogo } from '../branding/brandLogoUtils';
import AppSwitcher from '../auth/AppSwitcher';
import { clearSessionAuth, getSessionAuth, applySessionClaims, authHeaders } from '../auth/session';
import { serverURL } from '../../constants';
import axios from 'axios';
import {
    isFocusCollapseShellPath,
    isImmersiveShellPath,
    isViewportFillShellPath,
    shouldForceCollapseRail,
} from './roleShell';
import DarkModeToggle from '../../components/DarkModeToggle';
import LanguageSwitcher from '../../components/LanguageSwitcher';

function formatTokenCount(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 10_000) return `${Math.round(v / 1000)}k`;
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return String(Math.round(v));
}

export function PageHeader({ title, subtitle, actions, breadcrumb }) {
    return (
        <header className="mb-6">
            {breadcrumb ? (
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">{breadcrumb}</div>
            ) : null}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-[1.5rem] font-semibold tracking-tight text-slate-900 dark:text-slate-50 leading-tight">
                        {title}
                    </h1>
                    {subtitle ? (
                        <div className="mt-1 text-sm text-slate-500 dark:text-slate-400 leading-relaxed max-w-2xl">{subtitle}</div>
                    ) : null}
                </div>
                {actions ? (
                    <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>
                ) : null}
            </div>
        </header>
    );
}

export function Card({ children, className = '', padded = false }) {
    return (
        <div
            className={`rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${
                padded ? 'p-5 sm:p-6' : ''
            } ${className}`}
        >
            {children}
        </div>
    );
}

export function SectionTitle({ title, action }) {
    return (
        <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
            {action || null}
        </div>
    );
}

export function Alert({ tone = 'error', children }) {
    const tones = {
        error: 'bg-red-50/90 dark:bg-red-950/40 text-red-800 dark:text-red-200 border-red-100 dark:border-red-900/50',
        success: 'bg-emerald-50/90 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 border-emerald-100 dark:border-emerald-900/50',
        warn: 'bg-amber-50/90 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100 border-amber-100 dark:border-amber-900/50',
        info: 'bg-sky-50/90 dark:bg-sky-950/40 text-sky-900 dark:text-sky-100 border-sky-100 dark:border-sky-900/50',
    };
    return (
        <div className={`mb-5 rounded-xl border px-3.5 py-2.5 text-sm leading-relaxed ${tones[tone] || tones.info}`}>
            {children}
        </div>
    );
}

export function EmptyState({ title, description, action }) {
    return (
        <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 px-6 py-14 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-400">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    <path d="M4 7h16M4 12h10M4 17h7" strokeLinecap="round" />
                </svg>
            </div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</div>
            {description ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">{description}</p> : null}
            {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
        </div>
    );
}

export function Badge({ children, tone = 'slate' }) {
    const tones = {
        slate: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 ring-slate-200/60 dark:ring-slate-700',
        blue: 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-200 ring-blue-100 dark:ring-blue-900/50',
        green: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-200 ring-emerald-100 dark:ring-emerald-900/50',
        amber: 'bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-100 ring-amber-100 dark:ring-amber-900/50',
        red: 'bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-200 ring-red-100 dark:ring-red-900/50',
        violet: 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-200 ring-indigo-100 dark:ring-indigo-900/50',
    };
    return (
        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${tones[tone] || tones.slate}`}>
            {children}
        </span>
    );
}

export function StatGrid({ stats = [] }) {
    return (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
            {stats.map((s) => (
                <Card key={s.label} className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-2">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500 dark:text-slate-400">
                            {s.label}
                        </div>
                        {s.icon ? (
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-100 dark:border-slate-700">
                                {s.icon}
                            </div>
                        ) : null}
                    </div>
                    <div className="mt-2 text-[1.65rem] font-semibold tracking-tight text-slate-900 dark:text-slate-50 tabular-nums leading-none">
                        {s.value}
                    </div>
                    {s.hint ? <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{s.hint}</div> : null}
                </Card>
            ))}
        </div>
    );
}

export function Btn({ children, variant = 'primary', className = '', type = 'button', size = 'md', ...props }) {
    const sizes = {
        sm: 'px-3 py-1.5 text-xs rounded-lg',
        md: 'px-3.5 py-2 text-sm rounded-lg',
    };
    const base = `inline-flex items-center justify-center gap-1.5 font-semibold transition disabled:opacity-45 disabled:pointer-events-none ${sizes[size] || sizes.md}`;
    const variants = {
        primary: 'text-white shadow-sm shadow-blue-600/15 hover:brightness-105 active:brightness-95',
        secondary: 'border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300',
        danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm',
        ghost: 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white',
    };
    const style = variant === 'primary'
        ? { backgroundColor: 'var(--org-primary, #2563eb)' }
        : undefined;
    return (
        <button type={type} className={`${base} ${variants[variant] || variants.primary} ${className}`} style={style} {...props}>
            {children}
        </button>
    );
}

export function Field({ label, children, hint }) {
    return (
        <label className="block text-sm text-slate-700 dark:text-slate-300">
            <span className="font-medium text-slate-800 dark:text-slate-100">{label}</span>
            <div className="mt-1.5">{children}</div>
            {hint ? <span className="mt-1.5 block text-xs text-slate-500 dark:text-slate-400">{hint}</span> : null}
        </label>
    );
}

export const inputClass =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 shadow-sm shadow-slate-900/[0.02] focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition';

export function Toolbar({ children, className = '' }) {
    return (
        <div className={`mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`}>
            {children}
        </div>
    );
}

export function SearchInput({ value, onChange, placeholder, className = '' }) {
    const { t } = useTranslation();
    const resolvedPlaceholder = placeholder ?? t('shell.chrome.searchPlaceholder');
    return (
        <div className={`relative ${className}`}>
            <svg
                className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-400"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
            >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3-3" strokeLinecap="round" />
            </svg>
            <input
                className={`${inputClass} ps-9`}
                value={value}
                onChange={onChange}
                placeholder={resolvedPlaceholder}
            />
        </div>
    );
}

export function DataTable({
    columns = [],
    rows = [],
    emptyTitle,
    emptyDescription,
    rowKey = '_id',
    showCount = true,
    footer = null,
    onRowClick,
}) {
    const { t } = useTranslation();
    const resolvedEmptyTitle = emptyTitle ?? t('shell.chrome.noRows');
    if (!rows.length) {
        return <EmptyState title={resolvedEmptyTitle} description={emptyDescription} />;
    }
    return (
        <Card className="overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="border-b border-slate-200/80 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-800/60">
                            {columns.map((c) => (
                                <th
                                    key={c.key || c.label}
                                    className="px-4 py-3 text-start text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500 dark:text-slate-400 whitespace-nowrap"
                                >
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {rows.map((row, idx) => (
                            <tr
                                key={row[rowKey] || idx}
                                className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                            >
                                {columns.map((c) => (
                                    <td
                                        key={c.key || c.label}
                                        className="px-4 py-3.5 align-middle text-slate-700 dark:text-slate-300"
                                        onClick={c.stopRowClick ? (e) => e.stopPropagation() : undefined}
                                    >
                                        {c.render ? c.render(row) : row[c.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {footer != null ? footer : showCount ? (
                <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 bg-slate-50/40 dark:bg-slate-900/50">
                    {rows.length}{' '}
                    {rows.length === 1 ? t('shell.chrome.row') : t('shell.chrome.rows')}
                </div>
            ) : null}
        </Card>
    );
}

const MODAL_SIZES = {
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-3xl',
};

export function Modal({ open, title, children, onClose, footer, size = 'md' }) {
    const { t } = useTranslation();
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
            <button type="button" className="absolute inset-0 bg-slate-900/45 dark:bg-slate-950/70 backdrop-blur-[2px]" aria-label={t('shell.chrome.close')} onClick={onClose} />
            <div className={`relative w-full ${MODAL_SIZES[size] || MODAL_SIZES.md} rounded-2xl bg-white dark:bg-slate-900 shadow-2xl shadow-slate-900/20 border border-slate-200/80 dark:border-slate-700 overflow-hidden animate-[fadeIn_120ms_ease-out]`}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                    <h2 className="text-[15px] font-semibold text-slate-900 dark:text-slate-50">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition"
                        aria-label={t('shell.chrome.closeDialog')}
                    >
                        ×
                    </button>
                </div>
                <div className="px-5 py-4 max-h-[70vh] overflow-y-auto text-slate-700 dark:text-slate-200">{children}</div>
                {footer ? (
                    <div className="px-5 py-3.5 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2 bg-slate-50/70 dark:bg-slate-950/50">
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function NavIcon({ d }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
            <path d={d} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export function LoadingBlock({ rows = 3 }) {
    return (
        <div className="space-y-3" aria-busy="true">
            {[...Array(rows)].map((_, i) => (
                <div key={i} className="h-12 rounded-xl bg-slate-100/80 animate-pulse" />
            ))}
        </div>
    );
}

/** Dashboard-style skeleton for Super Admin / Org list pages. */
export function PageSkeleton({ cards = 4, tableRows = 5 }) {
    return (
        <div aria-busy="true">
            {cards > 0 ? (
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
                    {[...Array(cards)].map((_, i) => (
                        <Card key={i} className="p-5 h-[96px] animate-pulse bg-slate-100/60 border-slate-100" />
                    ))}
                </div>
            ) : null}
            <Card className="overflow-hidden">
                <div className="border-b border-slate-100 px-4 py-3 bg-slate-50/80">
                    <div className="h-3 w-40 rounded bg-slate-200/80 animate-pulse" />
                </div>
                <div className="p-4 space-y-3">
                    {[...Array(tableRows)].map((_, i) => (
                        <div key={i} className="h-10 rounded-lg bg-slate-100/80 animate-pulse" />
                    ))}
                </div>
            </Card>
        </div>
    );
}

/**
 * Role app shell — consistent chrome for learner / studio / org / platform.
 * navItems: { to, label, end?, icon? } or { section: 'Label' }
 */
export default function AppShell({
    roleLabel,
    navItems = [],
    footerLinks = [],
    contentMaxClass = 'max-w-5xl',
    variant = 'default',
    shell = null,
    profileTo = null,
    showCapacity = true,
}) {
    const { t } = useTranslation();
    const { branding, organization, loading: brandingLoading } = useOrganization();
    const [, setSessionTick] = useState(0);
    const auth = getSessionAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [capacity, setCapacity] = useState({ used: null, limit: null, kind: 'courses' });
    const [aiTokens, setAiTokens] = useState({ used: null, limit: null, remaining: null });
    const planType = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('type')) || 'free';
    const isFreePlan = planType === 'free' || !planType;
    const signOutLabel = t('shell.chrome.signOut');
    const signedInFallback = t('shell.chrome.signedIn');
    const brandName = branding?.companyName || organization?.name || (brandingLoading ? '' : 'Coursolo');
    // Never place Coursolo.ai beside the logo — the wordmark already includes it.
    const showBrandName = Boolean(brandName) && shouldShowBrandNameWithLogo(branding, brandName);
    const emailLocal = auth.email ? String(auth.email).split('@')[0] : '';
    const rawName = auth.mName ? String(auth.mName).trim() : '';
    const looksLikeGreeting = /^(hello|hi|hey|welcome)$/i.test(rawName);
    // Prefer a real name; otherwise show email only (avoid "hello" + hello@…)
    const hasRealName = Boolean(rawName && !looksLikeGreeting && rawName.toLowerCase() !== emailLocal.toLowerCase());
    const userDisplayName = hasRealName ? rawName : '';
    const accountPrimary = userDisplayName || auth.email || signedInFallback;
    const accountSecondary = userDisplayName && auth.email ? auth.email : null;
    const userInitial = (userDisplayName || emailLocal || auth.email || '?').charAt(0).toUpperCase();
    const isAdmin = variant === 'admin';
    const resolvedProfileTo = profileTo || (shell === 'learn' ? '/learn/profile' : null);

    useEffect(() => {
        const { token, organizationId, orgRole } = getSessionAuth();
        if (!token || (organizationId && orgRole)) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${serverURL}/api/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (cancelled || !data?.success) return;
                applySessionClaims({
                    organizationId: data.organizationId,
                    orgRole: data.orgRole,
                    platformAdmin: data.platformAdmin,
                    role: data.user?.role,
                    email: data.user?.email,
                    mName: data.user?.mName,
                    uid: data.user?._id,
                });
                setSessionTick((n) => n + 1);
            } catch {
                /* ignore — pages will surface auth errors */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Throttled presence heartbeat so super-admin can see last active / last app.
    useEffect(() => {
        const { token } = getSessionAuth();
        if (!token) return undefined;
        const app =
            shell === 'learn' || shell === 'studio' || shell === 'org' || shell === 'platform'
                ? shell
                : variant === 'admin'
                    ? 'platform'
                    : 'unknown';
        const ping = () => {
            axios
                .post(`${serverURL}/api/auth/presence`, { app }, { headers: authHeaders() })
                .catch(() => {});
        };
        ping();
        const id = window.setInterval(ping, 15 * 60 * 1000);
        const onFocus = () => ping();
        window.addEventListener('focus', onFocus);
        return () => {
            window.clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
    }, [shell, variant]);

    useEffect(() => {
        if (!showCapacity) return undefined;
        let cancelled = false;
        (async () => {
            const { uid, token } = getSessionAuth();
            if (!uid || !token) {
                if (!cancelled) {
                    setCapacity({ used: isFreePlan ? 0 : null, limit: isFreePlan ? 3 : null, kind: 'courses' });
                    setAiTokens({ used: 0, limit: isFreePlan ? 30000 : null, remaining: isFreePlan ? 30000 : null });
                }
                return;
            }
            try {
                const { data } = await axios.get(`${serverURL}/api/user/videocount/${uid}`, {
                    headers: authHeaders(),
                });
                if (cancelled) return;
                const maxCourses = data?.limits?.maxCourses;
                const maxVideos = data?.limits?.maxVideoCourses;
                if (maxCourses != null) {
                    setCapacity({
                        used: Number(data?.courseCount ?? data?.courseQuota?.used ?? 0),
                        limit: Number(maxCourses),
                        kind: 'courses',
                    });
                } else if (maxVideos != null && maxVideos > 0) {
                    setCapacity({
                        used: Number(data?.videoCount || 0),
                        limit: Number(maxVideos),
                        kind: 'videos',
                    });
                } else if (isFreePlan) {
                    setCapacity({
                        used: Number(data?.courseCount ?? data?.videoCount ?? 0),
                        limit: 3,
                        kind: 'courses',
                    });
                } else {
                    setCapacity({ used: Number(data?.courseCount || data?.videoCount || 0), limit: null, kind: 'courses' });
                }

                const ai = data?.aiQuota;
                if (ai) {
                    setAiTokens({
                        used: ai.used ?? 0,
                        limit: ai.limit ?? null,
                        remaining: ai.remaining ?? null,
                    });
                } else if (data?.limits?.aiTokensPerMonth != null) {
                    const limit = Number(data.limits.aiTokensPerMonth);
                    setAiTokens({ used: 0, limit: limit, remaining: limit });
                } else {
                    setAiTokens({ used: 0, limit: null, remaining: null });
                }
            } catch {
                if (!cancelled) {
                    setCapacity({ used: 0, limit: isFreePlan ? 3 : null, kind: 'courses' });
                    setAiTokens({ used: 0, limit: isFreePlan ? 30000 : null, remaining: isFreePlan ? 30000 : null });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [showCapacity, location.pathname, isFreePlan]);

    const logout = () => {
        const headers = authHeaders();
        // Best-effort server log before clearing local session.
        axios.post(`${serverURL}/api/auth/logout`, {}, { headers }).catch(() => {});
        clearSessionAuth();
        window.location.href = '/signin';
    };

    const pageTitle = [...navItems].reverse().find((n) => n.to && location.pathname.startsWith(n.to) && !(n.end && location.pathname !== n.to))?.label;
    const immersive = isImmersiveShellPath(location.pathname);
    const viewportFill = isViewportFillShellPath(location.pathname);
    const focusMode = isFocusCollapseShellPath(location.pathname);

    // Focus surfaces (course, exam, genui, practice, …): start icons-only; user can expand labels.
    // Collapse only when *entering* focus — keep expand while navigating within focus routes.
    const [railExpanded, setRailExpanded] = useState(false);
    const wasFocusModeRef = useRef(focusMode);

    useEffect(() => {
        if (shouldForceCollapseRail(wasFocusModeRef.current, focusMode)) {
            setRailExpanded(false);
        }
        wasFocusModeRef.current = focusMode;
    }, [focusMode]);

    const toggleRail = () => {
        setRailExpanded((prev) => !prev);
    };

    const compact = focusMode && !railExpanded;
    const showLabels = !compact;
    const railToggleTitle = railExpanded
        ? t('shell.chrome.collapseMenu')
        : t('shell.chrome.expandMenu');

    const linkClass = ({ isActive }) =>
        compact
            ? `group relative flex items-center justify-center rounded-xl h-10 w-10 mx-auto transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 ${
                isActive
                    ? 'text-white'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100'
            }`
            : `group relative flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 ${
                isActive
                    ? 'text-white'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-white'
            }`;

    const activeNavStyle = (isActive) => (
        isActive ? { backgroundColor: 'var(--org-primary, #2563eb)' } : undefined
    );

    const brandMark = (
        <OrgBrandLogo
            branding={branding}
            className={`${
                compact
                    ? 'h-8 w-8'
                    : 'h-9 w-auto max-w-full'
            } object-contain object-start shrink-0`}
            alt={brandName}
        />
    );

    // Logo = brand. Role + person live elsewhere (workspace chip / account footer).
    const brandBlock = (
        <div className={`shrink-0 ${compact ? 'px-0 mb-2' : 'px-0.5 mb-2.5'}`}>
            {compact ? (
                <div className="flex justify-center py-0.5">{brandMark}</div>
            ) : (
                <div className="space-y-1.5 px-1">
                    <div className="flex items-center gap-2.5 min-h-[2rem]">
                        {brandMark}
                        {showBrandName ? (
                            <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate leading-tight">
                                {brandName}
                            </p>
                        ) : null}
                    </div>
                    <span className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-500 dark:text-slate-400">
                        {roleLabel}
                    </span>
                </div>
            )}
            {showLabels ? (
                <div className={`${compact ? 'mt-2' : 'mt-3'} px-0.5`}>
                    <AppSwitcher />
                </div>
            ) : null}
            {focusMode ? (
                <button
                    type="button"
                    onClick={toggleRail}
                    title={railToggleTitle}
                    aria-label={railToggleTitle}
                    aria-expanded={railExpanded}
                    className={`mt-2 flex items-center rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100 transition ${
                        compact
                            ? 'justify-center h-9 w-9 mx-auto border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/60'
                            : 'gap-2 w-full px-2.5 py-1.5 text-[12px] font-medium'
                    }`}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden className="shrink-0 rtl:-scale-x-100">
                        {railExpanded ? (
                            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                        ) : (
                            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                        )}
                    </svg>
                    {showLabels ? (
                        <span>{railExpanded ? t('shell.chrome.collapse') : t('shell.chrome.expand')}</span>
                    ) : null}
                </button>
            ) : null}
        </div>
    );

    const navLinks = (
        <nav
            className={`flex-1 min-h-0 overscroll-contain pb-2 ${
                compact
                    ? 'space-y-1 px-0 overflow-visible'
                    : 'space-y-0.5 px-0.5 overflow-y-auto'
            }`}
            aria-label={roleLabel}
        >
            {navItems.map((item, i) => {
                if (item.section) {
                    if (compact) {
                        return i === 0 ? null : (
                            <div key={`sec-${item.section}-${i}`} className="mx-2 my-2 border-t border-slate-100 dark:border-slate-800" aria-hidden />
                        );
                    }
                    return (
                        <div
                            key={`sec-${item.section}-${i}`}
                            role="presentation"
                            className="pt-3.5 first:pt-0.5 pb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500"
                        >
                            {item.section}
                        </div>
                    );
                }
                return (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={linkClass}
                        style={({ isActive }) => activeNavStyle(isActive)}
                        onClick={(e) => {
                            setMobileOpen(false);
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                            e.preventDefault();
                            startTransition(() => navigate(item.to));
                        }}
                        title={compact ? undefined : (item.hint || item.label)}
                        aria-label={item.hint ? `${item.label}. ${item.hint}` : item.label}
                    >
                        {item.icon ? (
                            <span className={`opacity-90 ${compact ? 'scale-105' : ''}`} aria-hidden>{item.icon}</span>
                        ) : (
                            <span className="text-[11px] font-semibold" aria-hidden>{(item.label || '?').slice(0, 1)}</span>
                        )}
                        {showLabels ? <span className="truncate">{item.label}</span> : null}
                        {compact ? (
                            <span
                                aria-hidden
                                className="pointer-events-none absolute start-full top-1/2 z-50 ms-2 -translate-y-1/2 whitespace-nowrap rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-200 shadow-sm opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                            >
                                {item.label}
                            </span>
                        ) : null}
                    </NavLink>
                );
            })}
        </nav>
    );

    const usedCapacity = capacity.used ?? 0;
    const capacityLimit = capacity.limit;
    const remainingCapacity = capacityLimit == null ? null : Math.max(0, capacityLimit - usedCapacity);
    const capacityPct = capacityLimit != null
        ? Math.min(100, Math.round((usedCapacity / Math.max(1, capacityLimit)) * 100))
        : null;
    const planLabel = planType === 'free'
        ? t('shell.chrome.planFree')
        : planType === 'forever'
            ? t('shell.chrome.planForever')
            : t('shell.chrome.planPremium');

    const aiUsed = aiTokens.used ?? 0;
    const aiLimit = aiTokens.limit;
    const aiRemaining = aiLimit == null ? null : Math.max(0, (aiTokens.remaining ?? (aiLimit - aiUsed)));
    const aiPct = aiLimit != null
        ? Math.min(100, Math.round((aiUsed / Math.max(1, aiLimit)) * 100))
        : null;
    const usageTight = remainingCapacity === 0 || aiRemaining === 0;

    const creditsSummary = capacityLimit == null
        ? t('shell.chrome.capacityUnlimitedShort')
        : t('shell.chrome.creditsOf', { used: usedCapacity, limit: capacityLimit });
    const tokensSummary = aiLimit == null
        ? t('shell.chrome.capacityUnlimitedShort')
        : t('shell.chrome.tokensLeftOf', { left: formatTokenCount(aiRemaining) });

    const miniMeter = (pct, depleted, label) => (
        <div
            className="mt-1 h-1 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct == null ? undefined : pct}
            aria-label={label}
        >
            <div
                className={`h-full rounded-full transition-all ${depleted ? 'bg-amber-500' : ''}`}
                style={{
                    width: `${pct == null ? 100 : pct}%`,
                    backgroundColor: depleted || pct == null ? undefined : 'var(--org-primary, #2563eb)',
                    ...(pct == null && !depleted ? { backgroundColor: '#34d399' } : null),
                }}
            />
        </div>
    );

    const sidebarFooter = (
        <footer
            className={`shrink-0 border-t border-slate-200 dark:border-slate-800 ${compact ? 'pt-2 space-y-2' : 'pt-2.5 px-0.5 space-y-2'}`}
            aria-label={t('shell.chrome.accountArea')}
        >
            {showCapacity && showLabels ? (
                <section
                    className="rounded-lg bg-slate-50 dark:bg-slate-900/50 px-2 py-1.5"
                    aria-label={t('shell.chrome.usage')}
                >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-slate-500 dark:text-slate-400">
                            {t('shell.chrome.planLabel', { plan: planLabel })}
                        </p>
                        {isFreePlan ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setMobileOpen(false);
                                    startTransition(() => navigate('/pricing'));
                                }}
                                className={`text-[11px] font-semibold rounded-md px-1.5 py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 transition ${
                                    usageTight
                                        ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 dark:text-amber-300 dark:bg-amber-950/40'
                                        : 'text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/40'
                                }`}
                            >
                                {t('shell.chrome.upgradeShort')}
                            </button>
                        ) : null}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="flex items-baseline justify-between gap-1">
                                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                                    {t('shell.chrome.creditsShort')}
                                </span>
                                <span className={`text-[11px] font-semibold tabular-nums ${remainingCapacity === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                    {capacityLimit == null ? '∞' : `${usedCapacity}/${capacityLimit}`}
                                </span>
                            </div>
                            {miniMeter(capacityPct, remainingCapacity === 0, creditsSummary)}
                            <span className="sr-only">{creditsSummary}</span>
                        </div>
                        <div>
                            <div className="flex items-baseline justify-between gap-1">
                                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                                    {t('shell.chrome.tokensShort')}
                                </span>
                                <span className={`text-[11px] font-semibold tabular-nums ${aiRemaining === 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                    {aiLimit == null ? '∞' : formatTokenCount(aiRemaining)}
                                </span>
                            </div>
                            {miniMeter(aiPct, aiRemaining === 0, tokensSummary)}
                            <span className="sr-only">{tokensSummary}</span>
                        </div>
                    </div>
                </section>
            ) : null}

            <div
                className={`flex items-center ${compact ? 'flex-col gap-1' : 'justify-between gap-2 px-0.5'}`}
                role="group"
                aria-label={t('shell.chrome.preferences')}
            >
                <LanguageSwitcher compact variant="shell" />
                <DarkModeToggle
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 transition"
                    showLabel={!compact}
                />
            </div>

            {showLabels ? footerLinks.map((item) => (
                <NavLink key={item.to} to={item.to} className={linkClass} style={({ isActive }) => activeNavStyle(isActive)} onClick={() => setMobileOpen(false)}>
                    {item.label}
                </NavLink>
            )) : null}

            {compact ? (
                <div className="flex flex-col items-center gap-1" role="group" aria-label={t('shell.chrome.account')}>
                    {resolvedProfileTo ? (
                        <button
                            type="button"
                            onClick={() => { setMobileOpen(false); startTransition(() => navigate(resolvedProfileTo)); }}
                            title={t('shell.chrome.profile')}
                            aria-label={t('shell.chrome.profile')}
                            className="flex items-center justify-center h-9 w-9 rounded-full text-white text-[12px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 transition"
                            style={{ backgroundColor: 'var(--org-primary, #2563eb)' }}
                        >
                            {userInitial}
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={logout}
                        title={signOutLabel}
                        aria-label={signOutLabel}
                        className="flex items-center justify-center h-9 w-9 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 transition"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            ) : (
                <div
                    className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-1.5 py-1.5 min-w-0"
                    role="group"
                    aria-label={t('shell.chrome.account')}
                >
                    {resolvedProfileTo ? (
                        <button
                            type="button"
                            onClick={() => { setMobileOpen(false); startTransition(() => navigate(resolvedProfileTo)); }}
                            className="flex items-center gap-2 min-w-0 flex-1 rounded-lg px-1 py-0.5 text-start hover:bg-slate-50 dark:hover:bg-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 transition"
                            aria-label={t('shell.chrome.openProfile', { name: accountPrimary })}
                        >
                            <span
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white"
                                style={{ backgroundColor: 'var(--org-primary, #2563eb)' }}
                                aria-hidden
                            >
                                {userInitial}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-[12px] font-semibold text-slate-800 dark:text-slate-100 truncate leading-tight">
                                    {accountPrimary}
                                </span>
                                {accountSecondary ? (
                                    <span className="block text-[10px] text-slate-500 dark:text-slate-400 truncate leading-tight mt-0.5">
                                        {accountSecondary}
                                    </span>
                                ) : (
                                    <span className="block text-[10px] text-slate-400 dark:text-slate-500 truncate leading-tight mt-0.5">
                                        {t('shell.chrome.viewProfile')}
                                    </span>
                                )}
                            </span>
                        </button>
                    ) : (
                        <div className="flex items-center gap-2 min-w-0 flex-1 px-1">
                            <span
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white"
                                style={{ backgroundColor: 'var(--org-primary, #2563eb)' }}
                                aria-hidden
                            >
                                {userInitial}
                            </span>
                            <span className="min-w-0 flex-1 text-[12px] font-semibold text-slate-800 dark:text-slate-100 truncate">
                                {accountPrimary}
                            </span>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={logout}
                        title={signOutLabel}
                        aria-label={signOutLabel}
                        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 transition"
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            )}
        </footer>
    );

    const asideWidthClass = focusMode
        ? (railExpanded ? 'w-[15.5rem] p-3.5' : 'w-[4.25rem] p-2')
        : 'w-[15.5rem] p-3.5';

    return (
        <div
            className={`min-h-screen flex text-slate-900 dark:text-slate-100 ${isAdmin ? 'bg-[#f6f7f9] dark:bg-[#0b0d12]' : 'bg-slate-50 dark:bg-slate-950'} ${viewportFill ? 'h-[100dvh] max-h-[100dvh] overflow-hidden' : ''}`}
            style={{
                ...(branding?.primaryColor ? { '--org-primary': branding.primaryColor } : null),
                ...(branding?.secondaryColor ? { '--org-secondary': branding.secondaryColor } : null),
                fontFamily: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
            }}
        >
            <aside
                data-app-shell-rail="true"
                className={`hidden lg:flex shrink-0 flex-col border-e border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 sticky top-0 h-[100dvh] max-h-[100dvh] transition-[width,padding] duration-200 ease-out ${
                    compact ? 'overflow-visible z-30' : 'overflow-hidden'
                } ${asideWidthClass}`}
            >
                {brandBlock}
                {navLinks}
                {sidebarFooter}
            </aside>

            <div className={`lg:hidden fixed inset-x-0 top-0 z-40 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur supports-[backdrop-filter]:bg-white/85 dark:supports-[backdrop-filter]:bg-slate-950/85 ${viewportFill ? 'hidden' : ''}`}>
                <div className="flex items-center justify-between gap-2 px-3 sm:px-4 h-14 sm:h-16 min-w-0">
                    <div className="flex items-center min-w-0 flex-1 gap-2">
                        <OrgBrandLogo
                            branding={branding}
                            className="h-9 sm:h-10 w-auto max-w-[min(8rem,36vw)] object-contain shrink-0"
                            alt={brandName}
                        />
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <LanguageSwitcher compact variant="topbar" />
                        <DarkModeToggle className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition" />
                        <button
                            type="button"
                            className="ms-0.5 rounded-xl border border-slate-200 dark:border-slate-700 px-2.5 sm:px-3 py-1.5 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 min-h-[2.25rem]"
                            onClick={() => setMobileOpen((v) => !v)}
                        >
                            {t('shell.chrome.menu')}
                        </button>
                    </div>
                </div>
                {mobileOpen ? (
                    <div className="border-t border-slate-100 dark:border-slate-800 p-3 max-h-[min(75vh,calc(100dvh-3.5rem))] overflow-hidden bg-white dark:bg-slate-950 flex flex-col">
                        <div className="px-1 mb-3 space-y-2 min-w-0">
                            <OrgBrandLogo
                                branding={branding}
                                className="h-9 w-auto max-w-[min(12rem,70vw)] object-contain object-start shrink-0"
                                alt={brandName}
                            />
                            {showBrandName ? (
                                <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate">
                                    {brandName}
                                </p>
                            ) : null}
                            <span className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-500 dark:text-slate-400">
                                {roleLabel}
                            </span>
                        </div>
                        <div className="px-0.5 mb-3"><AppSwitcher /></div>
                        <nav className="flex-1 min-h-0 space-y-0.5 overflow-y-auto overscroll-contain px-0.5 pb-2">
                            {navItems.map((item, i) => {
                                if (item.section) {
                                    return (
                                        <div
                                            key={`m-sec-${item.section}-${i}`}
                                            className="pt-3.5 first:pt-0.5 pb-1 px-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500"
                                        >
                                            {item.section}
                                        </div>
                                    );
                                }
                                return (
                                    <NavLink
                                        key={`m-${item.to}`}
                                        to={item.to}
                                        end={item.end}
                                        className={linkClass}
                                        style={({ isActive }) => activeNavStyle(isActive)}
                                        onClick={(e) => {
                                            setMobileOpen(false);
                                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                                            e.preventDefault();
                                            startTransition(() => navigate(item.to));
                                        }}
                                        title={item.hint || item.label}
                                    >
                                        {item.icon ? <span className="opacity-90">{item.icon}</span> : null}
                                        <span className="truncate">{item.label}</span>
                                    </NavLink>
                                );
                            })}
                        </nav>
                        {sidebarFooter ? (
                            <div className="shrink-0">
                                {sidebarFooter}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div
                className={`flex-1 min-w-0 flex flex-col ${viewportFill ? 'pt-0' : 'pt-14 lg:pt-0'} ${
                    viewportFill ? 'h-[100dvh] max-h-[100dvh] overflow-hidden' : ''
                }`}
                data-app-shell-main="true"
            >
                {!immersive ? (
                    <div className="hidden lg:flex h-14 shrink-0 items-center justify-between border-b border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 px-5 xl:px-8 relative z-20">
                        <div className="text-sm text-slate-500 dark:text-slate-400 flex items-center min-w-0">
                            <span className="text-slate-400 truncate">{roleLabel}</span>
                            <span className="mx-2 text-slate-300 dark:text-slate-600 shrink-0">/</span>
                            <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{pageTitle || t('shell.chrome.console')}</span>
                        </div>
                    </div>
                ) : null}

                <main
                    className={`flex-1 min-w-0 relative z-0 ${
                        viewportFill ? 'overflow-hidden flex flex-col min-h-0' : 'overflow-auto'
                    }`}
                >
                    <div
                        className={
                            immersive
                                ? (viewportFill
                                    ? 'flex-1 min-h-0 min-w-0 w-full h-full flex flex-col overflow-hidden'
                                    : 'w-full min-w-0 px-0 sm:px-0 py-0')
                                : `mx-auto w-full ${contentMaxClass} px-4 sm:px-6 lg:px-8 py-5 sm:py-6`
                        }
                    >
                        <Outlet context={{ embedded: true, shell: shell || roleLabel, immersive, viewportFill, focusMode, railExpanded }} />
                    </div>
                </main>
            </div>
        </div>
    );
}

export { NavIcon };
